import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import StatusBadge from '../components/ui/StatusBadge';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import ActionButton from '../components/ui/ActionButton';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';

const API = `${API_V1}/netting`;

interface Settlement { id: number; from_entity: string; to_entity: string; currency: string; net_amount: number; status: string; settlement_reference: string; }
interface Cycle { id: number; cycle_date: string; gross_amount: number; net_amount: number; fx_savings_estimate: number; entities_count: number; settlements_count: number; status: string; settlements: Settlement[]; }
interface Summary { total_cycles: number; total_gross: number; total_net: number; total_fx_savings: number; netting_efficiency: number; }

const thStyle: React.CSSProperties = { padding: '10px 14px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-hint)', borderBottom: '2px solid var(--border-subtle)', background: 'var(--bg-card)', whiteSpace: 'nowrap' };
const tdStyle: React.CSSProperties = { padding: '10px 14px', fontSize: 13, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-subtle)' };

export default function ICNetting() {
  const { entity } = useGlobalEntity();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [cycles, setCycles] = useState<Cycle[]>([]);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const showToast = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, cycRes] = await Promise.all([
        authFetch(withEntityParam(`${API}/summary`, entity)),
        authFetch(withEntityParam(`${API}/cycles`, entity)),
      ]);
      if (sumRes.ok) setSummary(await sumRes.json());
      if (cycRes.ok) { const d = await cycRes.json(); setCycles(d.cycles || []); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [entity]);

  useEffect(() => { load(); }, [load]);

  const handleNew = async () => { const res = await authFetch(`${API}/cycles`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }); if (res.ok) { showToast('Netting cycle created', true); load(); } else showToast('Failed', false); };
  const handleAction = async (id: number, action: string) => { const res = await authFetch(`${API}/cycles/${id}/${action}`, { method: 'POST' }); if (res.ok) { showToast(`Cycle ${action}d`, true); load(); } else { const e = await res.json(); showToast(e.detail || 'Failed', false); } };

  return (
    <PageLayout
      title="IC Netting"
      subtitle="Intercompany netting cycles, settlement matrix, and FX savings"
      action={{ label: '+ New Cycle', onClick: handleNew }}
    >
      {summary && (
        <KpiGrid
          columns={5}
          items={[
            { label: 'Total Cycles', value: summary.total_cycles },
            { label: 'Gross Amount', value: formatAmountCompact(summary.total_gross) },
            { label: 'Net Amount', value: formatAmountCompact(summary.total_net), color: 'var(--accent)' },
            { label: 'FX Savings', value: formatAmountCompact(summary.total_fx_savings), color: 'var(--positive)' },
            { label: 'Efficiency', value: `${summary.netting_efficiency}%`, color: 'var(--positive)' },
          ]}
        />
      )}

      {loading ? (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>Loading...</div>
      ) : cycles.length === 0 ? (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>No netting cycles.</div>
      ) : (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Date', 'Gross', 'Net', 'Savings', 'Entities', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ ...thStyle, textAlign: ['Gross', 'Net', 'Savings'].includes(h) ? 'right' : 'left' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cycles.map(c => (
                <>
                  <tr
                    key={c.id}
                    onClick={() => setExpandedId(expandedId === c.id ? null : c.id)}
                    style={{ cursor: 'pointer', background: expandedId === c.id ? 'var(--bg-hover)' : 'transparent', transition: 'background 0.1s' }}
                    onMouseEnter={e => { if (expandedId !== c.id) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                    onMouseLeave={e => { if (expandedId !== c.id) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{c.cycle_date}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{formatAmount(c.gross_amount)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{formatAmount(c.net_amount)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--positive)' }}>{formatAmount(c.fx_savings_estimate)}</td>
                    <td style={tdStyle}>{c.entities_count}</td>
                    <td style={tdStyle}><StatusBadge status={c.status} /></td>
                    <td style={tdStyle} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {c.status === 'DRAFT' && <ActionButton label="Approve" onClick={(e) => { e.stopPropagation(); handleAction(c.id, 'approve'); }} variant="success" size="sm" />}
                        {c.status === 'APPROVED' && <ActionButton label="Settle" onClick={(e) => { e.stopPropagation(); handleAction(c.id, 'settle'); }} size="sm" />}
                      </div>
                    </td>
                  </tr>
                  {expandedId === c.id && c.settlements && c.settlements.length > 0 && (
                    <tr key={`${c.id}-s`}>
                      <td colSpan={7} style={{ padding: '0 14px 12px 32px', background: 'var(--bg-hover)' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
                          <thead>
                            <tr>
                              {['From', 'To', 'Currency', 'Net Amount', 'Reference', 'Status'].map(h => (
                                <th key={h} style={{ ...thStyle, textAlign: h === 'Net Amount' ? 'right' : 'left', fontSize: 10, padding: '6px 10px' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {c.settlements.map(s => (
                              <tr key={s.id}>
                                <td style={{ ...tdStyle, fontWeight: 600, padding: '6px 10px' }}>{s.from_entity}</td>
                                <td style={{ ...tdStyle, padding: '6px 10px' }}>{s.to_entity}</td>
                                <td style={{ ...tdStyle, padding: '6px 10px' }}>{s.currency}</td>
                                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, padding: '6px 10px' }}>{formatAmount(s.net_amount)}</td>
                                <td style={{ ...tdStyle, color: 'var(--text-secondary)', fontSize: 11, padding: '6px 10px' }}>{s.settlement_reference || '-'}</td>
                                <td style={{ ...tdStyle, padding: '6px 10px' }}><StatusBadge status={s.status} /></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {toast && <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 2000, padding: '12px 20px', borderRadius: 8, background: toast.ok ? 'var(--positive-bg)' : 'var(--negative-bg)', color: toast.ok ? 'var(--positive)' : 'var(--negative)', fontSize: 13, fontWeight: 600 }}>{toast.msg}</div>}
    </PageLayout>
  );
}
